import { useQuery } from "@tanstack/react-query";
import { adminRequest } from "@/services/api/admin-request";
import { useAuthStore, selectIsAuthenticated } from "@/stores/auth-store";
import type { AdminApiError, AdminPermission } from "@/services/api/admin-registry";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminSection {
  key: string;
  label_key: string;
  icon: string;
  enabled: boolean;
  reason_disabled?: string;
}

export interface AdminCapabilities {
  can_view_dashboard?: boolean;
  can_manage_conversations?: boolean;
  can_manage_contacts?: boolean;
  can_manage_tools?: boolean;
  can_manage_models?: boolean;
  can_manage_settings?: boolean;
  can_view_audit?: boolean;
  can_manage_users?: boolean;
  can_manage_personas?: boolean;
  [key: string]: boolean | undefined;
}

export type PanelType = "tenant" | "platform";

function isValidPanelType(v: string): v is PanelType {
  return v === "tenant" || v === "platform";
}

interface AdminMeResponse {
  actor_role: string;
  is_admin: boolean;
  is_superadmin: boolean;
  permissions: string[]; // Backend sends dot notation, mapped to colon in hook
  capabilities: AdminCapabilities;
  tenant_id: string;
  email?: string;
  display_name?: string;
  sections: AdminSection[];
  panel_type?: PanelType;
}

export interface AdminAccessResult {
  isAdmin: boolean;
  isSuperadmin: boolean;
  isBasicUser: boolean;
  permissions: AdminPermission[];
  capabilities: AdminCapabilities;
  sections: AdminSection[];
  tenantId: string | null;
  panelType: PanelType;
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  error: AdminApiError | null;
}

// ---------------------------------------------------------------------------
// Fallback
// ---------------------------------------------------------------------------

const FALLBACK: Omit<AdminAccessResult, "isLoading" | "isError" | "isEmpty" | "error"> = {
  isAdmin: false,
  isSuperadmin: false,
  isBasicUser: false,
  permissions: [],
  capabilities: {},
  sections: [],
  tenantId: null,
  panelType: "tenant",
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAdminAccess(): AdminAccessResult {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  const { data, isLoading, error } = useQuery<AdminMeResponse, AdminApiError>({
    queryKey: ["admin", "me"],
    queryFn: async () => {
      const result = await adminRequest<AdminMeResponse>("admin.me");
      return result.data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, err) => {
      const noRetryKinds = ["auth", "forbidden", "not_found"];
      if (noRetryKinds.includes(err.kind)) return false;
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return { ...FALLBACK, isLoading: true, isError: false, isEmpty: false, error: null };
  }

  if (error) {
    console.error("[useAdminAccess] API error:", {
      status: error.httpStatus,
      kind: error.kind,
      correlationId: error.correlationId,
      details: error.details,
    });
    return { ...FALLBACK, isLoading: false, isError: true, isEmpty: false, error };
  }

  if (!data) {
    return { ...FALLBACK, isLoading: false, isError: false, isEmpty: true, error: null };
  }

  let panelType: PanelType = "tenant";
  if (data.panel_type && isValidPanelType(data.panel_type)) {
    panelType = data.panel_type;
  }

  // Backend sends dot notation (contacts.read), frontend uses colon (contacts:read)
  const permissions = data.permissions.map(
    (p) => p.replace(".", ":") as AdminPermission,
  );

  const sections = data.sections ?? [];
  const isEmpty = sections.length === 0;

  if (isEmpty && data.is_superadmin) {
    console.warn(
      "[useAdminAccess] Superadmin has empty sections — possible backend panel_type filter issue. All routes will be shown.",
    );
  } else if (isEmpty) {
    console.warn(
      "[useAdminAccess] Empty sections for non-superadmin — may indicate config issue",
    );
  }

  return {
    isAdmin: data.is_admin,
    isSuperadmin: data.is_superadmin,
    isBasicUser: !data.is_admin && !data.is_superadmin,
    permissions,
    capabilities: data.capabilities ?? {},
    sections,
    tenantId: data.tenant_id ?? null,
    panelType,
    isLoading: false,
    isError: false,
    isEmpty,
    error: null,
  };
}
